/*
 * Copyright (c) 2025. Bolmate B.V.
 */
import './style/cancelEmails.scss';

import React, {ForwardedRef, forwardRef, useContext, useImperativeHandle, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {ActivateHandle} from '../../../util/interfaces';

import {cancelAllEmails, getPlannedEmailsCount} from '../../../util/requestSender';

import {AppContext} from '../../misc/appContext';

import {Checkbox, CloseButton, Loader, ResponseData, WithBackground} from '@dvrd/dvr-controls';

import {createErrorMessage} from '../../../util/utils';

import {BolmateButton} from '../../controls';

import {showDialog, showSnackbar} from '@dvrd/dvr-controls';

function CancelEmails(props: object, ref: ForwardedRef<ActivateHandle>) {
    const {t} = useTranslation();
    const {account} = useContext(AppContext).accountContext;
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [count, setCount] = useState(-1);

    function onActivate() {
        if (!account) return;
        setActive(true);
        getPlannedEmailsCount({
            account_id: account.id,
            callback: (data: ResponseData) => {
                if (data.success) setCount(data.count);
            }
        });
    }

    function onClose() {
        setActive(false);
        setLoading(false);
        setConfirmed(false);
    }

    function onConfirm() {
        setConfirmed(true);
    }

    function onCancel() {
        if (!account) return;
        setLoading(true);
        cancelAllEmails({
            account_id: account.id,
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    onClose();
                    showSnackbar(t('email.cancelEmails.allCanceled', 'Alle ingeplande e-mails zijn geannuleerd'));
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t('email.cancelEmails.cancelFailed', 'Het annuleren van de e-mails is niet gelukt.')
                        ),
                        t('email.cancelEmails.cancelFailedTitle', 'Annuleren mislukt')
                    );
            }
        });
    }

    useImperativeHandle(ref, () => ({activate: onActivate}));

    return (
        <WithBackground active={active} onClose={onClose} preventBackgroundClick={loading} escapeDisabled={loading}>
            <div className='default-popup cancel-emails'>
                <div className='header-container'>
                    <label className='header-title'>{t('email.cancelEmails.title', 'E-mails annuleren')}</label>
                    <CloseButton onClick={onClose} disabled={loading} />
                </div>
                <div className='body-container'>
                    <Loader active={loading} />
                    <p className='body-message'>
                        {t('email.cancelEmails.body.prefix', 'Door de e-mails te annuleren worden')}{' '}
                        <b>
                            {t('email.cancelEmails.body.all', 'alle')}
                            {count <= 0 ? '' : ` (${count})`}
                        </b>{' '}
                        {t(
                            'email.cancelEmails.body.middle',
                            'ingeplande e-mails geannuleerd, zodat deze niet meer verzonden worden. Bijvoorbeeld wanneer de ingeplande e-mails een fout bevatten. Deze actie kan niet teruggedraaid worden en geannuleerde e-mails kunnen niet opnieuw ingepland worden.'
                        )}
                        <br />
                        {t(
                            'email.cancelEmails.body.keepOpenPrefix',
                            'Afhankelijk van het aantal ingeplande mails kan het annuleren een tijdje duren,'
                        )}{' '}
                        <b>
                            <u>{t('email.cancelEmails.body.keepOpen', 'houd de Emails pagina open')}</u>
                        </b>{' '}
                        {t(
                            'email.cancelEmails.body.keepOpenSuffix',
                            'om zeker te zijn dat alle e-mails geannuleerd worden.'
                        )}
                    </p>
                    <Checkbox
                        checked={confirmed}
                        onCheck={onConfirm}
                        label={t(
                            'email.cancelEmails.confirmCheckbox',
                            'Ik bevestig dat alle e-mails geannuleerd moeten worden en begrijp dat dit niet teruggedraaid kan worden'
                        )}
                    />
                </div>
                <div className='footer-container btn-2'>
                    <BolmateButton
                        onClick={onClose}
                        label={t('email.cancelEmails.close', 'Sluiten')}
                        disabled={loading}
                    />
                    <BolmateButton
                        onClick={onCancel}
                        label={t('email.cancelEmails.cancelBtn', 'E-mails annuleren')}
                        disabled={loading || !confirmed}
                        secondary
                    />
                </div>
            </div>
        </WithBackground>
    );
}

export default forwardRef<ActivateHandle, object>(CancelEmails);
